import { useMemo, useCallback } from 'react';
import DefaultTable from '..';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import RouterLink from 'src/components/ui/RouterLink';
import FilterPlateCDefault from 'src/components/ui/FilterPlate/connectors/FilterPlateCDefault';
import { VALUE_TYPE } from 'src/components/ui/FilterPlate/constants';
import { CELL_RENDER_TYPE } from 'src/components/ui/CellRender/constants';
import { COLUIMN_MIN_WIDTH } from '../constants';
import { FormattedMessage } from 'react-intl';
import {
	calcDuration,
	calcUnsolvedObjectionsDuration,
} from 'src/utils/helpers/calcDuration';
import {
	GORKO_STATUS_FILTER_OPTIONS,
	getGorkoStatusFilterLabel,
	mapCompanyGorkoStatus,
} from 'src/utils/mappers/companyGorkoStatus.mapper';
import {
	SALES_TARGET_FILTER_OPTIONS,
	formatWorthWithPriority,
	getSalesTargetLabel,
	mapCompanySalesTarget,
	resolveWorthValue,
} from 'src/utils/mappers/companySalesTarget.mapper';
import { formatCompanyStatusLabel } from 'src/utils/mappers/companyStatus.mapper';
import { useFilters } from 'src/hooks/filters/useFilters';
import {
	indexColumnsByCode,
	mapSelectedFilters,
} from 'src/hooks/filters/mapSelectedFilters';

const defultLinkGenerator = ({ id }) => `/company/${id}`;

const DefaultTableCBids = (props) => {
	const {
		generateLink = defultLinkGenerator,
		statuses = [],
		...rest
	} = props;
	const { getAllFilters } = useFilters();

	const ExpandLabel = useCallback(({ count, isExpanded }) => {
		if (isExpanded) return 'Свернуть';

		return (
			<FormattedMessage
				id="CellRender.duration.spaces"
				values={{ count }}
				defaultMessage="{count, plural, one {# связанная площадка} few {# связанные площадки} other {# связанных площадок}}"
			/>
		);
	}, []);

	const statusEnums = useMemo(
		() =>
			statuses.map((status) => ({
				value: status.id,
				label: status.name,
			})),
		[statuses],
	);

	const columns = useMemo(() => {
		return [
			{
				id: 'gorkoID',
				accessorFn: (data) => data.gorkoId,
				emptyValue: '—',
				header: 'Gorko ID',
				renderType: CELL_RENDER_TYPE.id,
				filter: {
					code: 'gorkoId',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'gorkoStatus',
				header: 'Статус',
				accessorFn: (data) => mapCompanyGorkoStatus(data),
				emptyValue: '—',
				minSize: COLUIMN_MIN_WIDTH.status,
				filter: {
					code: 'gorkoStatus',
					valueType: VALUE_TYPE.enum,
					payload: {
						enums: GORKO_STATUS_FILTER_OPTIONS,
					},
				},
			},
			{
				id: 'salesTarget',
				header: 'Цель продажи',
				accessorFn: (data) => mapCompanySalesTarget(data),
				emptyValue: '—',
				minSize: COLUIMN_MIN_WIDTH.salesTarget,
			},
			{
				id: 'name',
				header: 'Название',
				accessorFn: (data) => data.name,
				minSize: COLUIMN_MIN_WIDTH.company,
				ExpandLabel,
				filter: {
					code: 'name',
					valueType: VALUE_TYPE.string,
				},
				cell: (props) => {
					const val = props.getValue();
					const link = generateLink({ id: props.row.original.id });

					return (
						<Box>
							<Link to={link} component={RouterLink}>
								{val}
							</Link>
						</Box>
					);
				},
			},
			{
				id: 'cityName',
				header: 'Город',
				accessorFn: (data) => data.city?.name || null,
				filter: {
					code: 'cityName',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'worth',
				header: 'Ценность',
				accessorFn: (data) =>
					formatWorthWithPriority(
						resolveWorthValue(data.worth),
						data.commissionPriority,
					),
				filter: {
					code: 'worthValue',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'status',
				header: 'Статус продажи',
				accessorFn: (data) => formatCompanyStatusLabel(data),
				emptyValue: 'Не в работе',
				minSize: COLUIMN_MIN_WIDTH.status,
				filter: {
					code: 'status',
					valueType: VALUE_TYPE.enum,
					payload: {
						enums: statusEnums,
					},
				},
				cell: (props) => {
					const val = props.getValue();

					if (!val) return 'Не в работе';

					if (typeof val === 'string') return val;

					return (
						<Box>
							<Typography variant="body2">{val.label}</Typography>
							{val.remark && (
								<Typography variant="body2" color="secondary">
									{val.remark}
								</Typography>
							)}
						</Box>
					);
				},
			},
			{
				id: 'statusDuration',
				header: 'В статусе',
				accessorFn: (data) => calcDuration(data, 'statusAt'),
				renderType: CELL_RENDER_TYPE.duration,
				filter: {
					code: 'statusAt',
					valueType: VALUE_TYPE.date,
				},
			},
			{
				id: 'objectionsQtty',
				header: 'Возражения',
				emptyValue: 'Нет',
				renderType: CELL_RENDER_TYPE.number,
				accessorFn: (data) => data?.objections?.length || null,
				filter: {
					code: 'unsolvedObjectionsCount',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'objectionsDuration',
				header: 'Возр. не решено',
				emptyValue: '—',
				renderType: CELL_RENDER_TYPE.duration,
				accessorFn: (data) =>
					calcUnsolvedObjectionsDuration(data?.objections),
				filter: {
					code: 'latestUnsolvedObjectionCreatedAt',
					valueType: VALUE_TYPE.date,
				},
			},
			{
				id: 'updatedAt',
				header: 'Обновлено',
				accessorKey: 'updatedAt',
				emptyValue: '—',
				renderType: CELL_RENDER_TYPE.date,
				filter: {
					code: 'updatedAt',
					valueType: VALUE_TYPE.date,
				},
			},
		];
	}, [statusEnums, generateLink, ExpandLabel]);

	const columnsByCode = useMemo(() => indexColumnsByCode(columns), [columns]);

	const statusesById = useMemo(() => {
		const m = new Map();

		for (const s of statuses) {
			m.set(s.id, s);
			m.set(String(s.id), s);

			const numericId = Number(s.id);

			if (Number.isFinite(numericId)) {
				m.set(numericId, s);
			}
		}

		return m;
	}, [statuses]);

	const selectedFilters = useMemo(() => {
		return mapSelectedFilters(getAllFilters(), columnsByCode, (code) => {
			if (code === 'status') {
				return (value) =>
					statusesById.get(value)?.name ??
					statusesById.get(String(value))?.name ??
					statusesById.get(Number(value))?.name ??
					String(value);
			}

			if (code === 'gorkoStatus') {
				return getGorkoStatusFilterLabel;
			}

			if (code === 'salesTarget') {
				return getSalesTargetLabel;
			}

			return null;
		});
	}, [columnsByCode, statusesById, getAllFilters]);

	return (
		<DefaultTable
			columns={columns}
			selectedFilters={selectedFilters}
			FilterPlateRP={FilterPlateCDefault}
			{...rest}
		/>
	);
};

DefaultTableCBids.propTypes = {
	...DefaultTable.propTypes,
};

export default DefaultTableCBids;
